import React, { useEffect, useState } from 'react';

// material-ui
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

import { sendJson } from '../knowledgeFetch';

interface Props {
    /** The folder to add to; null when the dialog is closed. */
    folder: string | null;
    folderLabel: string;
    onClose: () => void;
    onCreated: (path: string) => void;
}

/** Names a new note in a folder the user may add to; the Hub validates the name and commits the note. */
const NewNoteDialog = (props: Props): JSX.Element => {
    const [name, setName] = useState('');
    const [error, setError] = useState<string>();
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setName('');
        setError(undefined);
    }, [props.folder]);

    const create = async () => {
        if (props.folder === null || name.trim() === '') {
            return;
        }
        setIsSaving(true);
        setError(undefined);
        try {
            const reply = await sendJson<{ path: string }>('/Knowledge/Create', 'POST', { folder: props.folder, name: name.trim(), body: `# ${name.trim()}\n` });
            if (reply.status === 201) {
                props.onCreated(reply.data.path);
                return;
            }
            setError(reply.data.error ?? `The note could not be created (${reply.status}).`);
        }
        catch {
            setError('The note could not be created. Try again in a minute.');
        }
        finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={props.folder !== null} onClose={props.onClose} fullWidth maxWidth="xs">
            <DialogTitle>New note</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>In {props.folderLabel}</Typography>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Name"
                    value={name}
                    inputProps={{ maxLength: 100 }}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
                    sx={{ mt: 1 }}
                />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Cancel</Button>
                <Button variant="contained" disabled={isSaving || name.trim() === ''} onClick={create}>Create</Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewNoteDialog;
